package com.havn.app.features.clients.presentation

import com.havn.app.core.logging.HavnLogger
import com.havn.app.core.media.HavnPickedPhoto
import com.havn.app.core.platform.HavnExternalActions
import com.havn.app.features.auth.domain.AuthRepository
import com.havn.app.features.auth.domain.AuthState
import com.havn.app.features.clients.domain.Client
import com.havn.app.features.clients.domain.ClientDraft
import com.havn.app.features.clients.domain.ClientRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

class ClientsViewModel(
    private val clientRepository: ClientRepository,
    private val authRepository: AuthRepository,
    private val scope: CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Main)
) {
    private val _uiState = MutableStateFlow(ClientsUiState())
    val uiState: StateFlow<ClientsUiState> = _uiState.asStateFlow()

    private var agentId: String? = null
    private var selectedAgencyId: String? = null
    private var hasLoadedAgencyContext = false

    init {
        scope.launch {
            authRepository.authState.collect { state ->
                val nextAgentId = (state as? AuthState.LoggedIn)?.user?.id
                agentId = nextAgentId
                if (nextAgentId == null) {
                    hasLoadedAgencyContext = false
                    _uiState.value = ClientsUiState()
                } else if (hasLoadedAgencyContext) {
                    loadClients(nextAgentId)
                }
            }
        }
    }

    fun refresh() {
        val currentAgentId = agentId ?: return
        scope.launch { loadClients(currentAgentId) }
    }

    fun updateSearchQuery(query: String) {
        if (_uiState.value.searchQuery == query) return
        _uiState.value = _uiState.value.copy(searchQuery = query, error = null)
        val currentAgentId = agentId ?: return
        scope.launch { loadClients(currentAgentId) }
    }

    fun loadMoreClients() {
        val currentAgentId = agentId ?: return
        val state = _uiState.value
        if (state.isLoading || state.isLoadingMore || !state.hasMoreClients) return
        scope.launch { loadClients(currentAgentId, append = true) }
    }

    fun setAgencyContext(agencyId: String?) {
        if (hasLoadedAgencyContext && selectedAgencyId == agencyId) return
        selectedAgencyId = agencyId
        hasLoadedAgencyContext = true
        val currentAgentId = agentId ?: return
        scope.launch { loadClients(currentAgentId) }
    }

    fun startAdd() {
        _uiState.value = _uiState.value.copy(
            mode = ClientsMode.Add,
            selectedClientId = null,
            form = ClientFormState(),
            message = null,
            error = null
        )
    }

    fun openClient(clientId: String) {
        val currentAgentId = agentId ?: return
        _uiState.value = _uiState.value.copy(
            mode = ClientsMode.Detail,
            selectedClientId = clientId,
            isDetailLoading = true,
            error = null
        )
        scope.launch {
            clientRepository.clientDetail(currentAgentId, clientId)
                .onSuccess {
                    replaceClient(it)
                    _uiState.value = _uiState.value.copy(isDetailLoading = false)
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isDetailLoading = false)
                    setError(it)
                }
        }
    }

    fun openList() {
        _uiState.value = _uiState.value.copy(mode = ClientsMode.List, selectedClientId = null, form = ClientFormState(), showDeleteConfirmation = false)
    }

    fun updateForm(transform: ClientFormState.() -> ClientFormState) {
        _uiState.value = _uiState.value.copy(form = _uiState.value.form.transform(), error = null)
    }

    fun saveClient() {
        val currentAgentId = agentId ?: return
        val state = _uiState.value
        val validation = state.form.validate()
        if (validation != null) {
            _uiState.value = state.copy(form = state.form.copy(validationError = validation))
            return
        }

        _uiState.value = state.copy(isSaving = true, error = null, message = null)
        scope.launch {
            clientRepository.addClient(currentAgentId, state.form.toDraft(), selectedAgencyId)
                .onSuccess { client ->
                    replaceClient(client)
                    _uiState.value = _uiState.value.copy(
                        isSaving = false,
                        mode = ClientsMode.Detail,
                        selectedClientId = client.id,
                        form = ClientFormState(),
                        message = "Contact created"
                    )
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isSaving = false)
                    setError(it)
                }
        }
    }

    fun startEdit() {
        val client = _uiState.value.selectedClient ?: return
        _uiState.value = _uiState.value.copy(
            mode = ClientsMode.Edit,
            form = client.toFormState(),
            message = null,
            error = null
        )
    }

    fun cancelForm() {
        val selectedId = _uiState.value.selectedClientId
        _uiState.value = _uiState.value.copy(
            mode = if (selectedId == null) ClientsMode.List else ClientsMode.Detail,
            form = ClientFormState(),
            error = null
        )
    }

    fun saveEditedClient() {
        val currentAgentId = agentId ?: return
        val clientId = _uiState.value.selectedClientId ?: return
        val state = _uiState.value
        val validation = state.form.validate()
        if (validation != null) {
            _uiState.value = state.copy(form = state.form.copy(validationError = validation))
            return
        }

        _uiState.value = state.copy(isSaving = true, error = null, message = null)
        scope.launch {
            clientRepository.updateClient(currentAgentId, clientId, state.form.toDraft())
                .onSuccess { client ->
                    replaceClient(client)
                    _uiState.value = _uiState.value.copy(
                        isSaving = false,
                        mode = ClientsMode.Detail,
                        selectedClientId = client.id,
                        form = ClientFormState(),
                        message = "Contact saved"
                    )
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isSaving = false)
                    setError(it)
                }
        }
    }

    fun requestDeleteSelected() {
        if (_uiState.value.selectedClientId == null) return
        _uiState.value = _uiState.value.copy(showDeleteConfirmation = true, error = null)
    }

    fun dismissDeleteConfirmation() {
        _uiState.value = _uiState.value.copy(showDeleteConfirmation = false)
    }

    fun confirmDeleteSelected() {
        val currentAgentId = agentId ?: return
        val clientId = _uiState.value.selectedClientId ?: return
        _uiState.value = _uiState.value.copy(isSaving = true, showDeleteConfirmation = false, error = null, message = null)
        scope.launch {
            clientRepository.deleteClient(currentAgentId, clientId)
                .onSuccess {
                    _uiState.value = _uiState.value.copy(
                        clients = _uiState.value.clients.filterNot { it.id == clientId },
                        selectedClientId = null,
                        mode = ClientsMode.List,
                        form = ClientFormState(),
                        isSaving = false
                    )
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isSaving = false)
                    setError(it)
                }
        }
    }

    fun sendQrCode(clientId: String) {
        val currentAgentId = agentId ?: return
        val client = _uiState.value.clients.firstOrNull { it.id == clientId } ?: return
        val body = "Your HAVN opening QR is attached. Add it to your phone wallet when Wallet pass delivery is enabled, or show the QR image at the opening."
        _uiState.value = _uiState.value.copy(isSaving = true, message = null, error = null)
        scope.launch {
            clientRepository.sendQrCode(currentAgentId, clientId)
                .onSuccess {
                    val opened = HavnExternalActions.shareQrImage(
                        title = "Your HAVN opening QR",
                        message = body,
                        fileName = "havn-${client.fullName.replace(" ", "-")}-qr.png",
                        payload = client.qrPayload,
                        email = client.email,
                        phone = client.phone
                    )
                    _uiState.value = _uiState.value.copy(
                        isSaving = false,
                        message = if (opened) "QR code ready to send" else null,
                        error = if (opened) null else "No email or SMS app is available for this contact."
                    )
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isSaving = false)
                    setError(it)
                }
        }
    }

    fun startEditingComments() {
        val client = _uiState.value.selectedClient ?: return
        _uiState.value = _uiState.value.copy(commentsDraft = client.notes.orEmpty(), isEditingComments = true)
    }

    fun updateCommentsDraft(value: String) {
        _uiState.value = _uiState.value.copy(commentsDraft = value, error = null)
    }

    fun saveComments() {
        val currentAgentId = agentId ?: return
        val clientId = _uiState.value.selectedClientId ?: return
        val comments = _uiState.value.commentsDraft
        _uiState.value = _uiState.value.copy(isSaving = true, message = null, error = null)
        scope.launch {
            clientRepository.updateClientNotes(currentAgentId, clientId, comments)
                .onSuccess { client ->
                    replaceClient(client)
                    _uiState.value = _uiState.value.copy(
                        isSaving = false,
                        isEditingComments = false,
                        commentsDraft = "",
                        message = "Comments saved"
                    )
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isSaving = false)
                    setError(it)
                }
        }
    }

    fun uploadClientPhoto(photo: HavnPickedPhoto?) {
        val currentAgentId = agentId ?: return
        val clientId = _uiState.value.selectedClientId ?: return
        if (photo == null || photo.bytes.isEmpty()) {
            _uiState.value = _uiState.value.copy(error = "Choose a valid contact photo.")
            return
        }
        _uiState.value = _uiState.value.copy(isSaving = true, message = null, error = null)
        scope.launch {
            clientRepository.updateClientPhoto(currentAgentId, clientId, photo)
                .onSuccess { client ->
                    replaceClient(client)
                    _uiState.value = _uiState.value.copy(isSaving = false, message = "Photo updated")
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isSaving = false)
                    setError(it)
                }
        }
    }

    fun dispose() {
        scope.cancel()
    }

    private suspend fun loadClients(agentId: String, append: Boolean = false) {
        val state = _uiState.value
        val offset = if (append) state.clients.size else 0
        _uiState.value = if (append) {
            state.copy(isLoadingMore = true, error = null)
        } else {
            state.copy(isLoading = true, error = null)
        }
        clientRepository.clientPage(
            agentId = agentId,
            agencyId = selectedAgencyId,
            query = _uiState.value.searchQuery,
            offset = offset,
            limit = CLIENT_PAGE_SIZE
        )
            .onSuccess { clients ->
                _uiState.value = _uiState.value.copy(
                    isLoading = false,
                    isLoadingMore = false,
                    clients = if (append) (_uiState.value.clients + clients).distinctBy { it.id } else clients,
                    hasMoreClients = clients.size == CLIENT_PAGE_SIZE
                )
            }
            .onFailure {
                _uiState.value = _uiState.value.copy(isLoading = false, isLoadingMore = false)
                setError(it)
            }
    }

    private fun replaceClient(client: Client) {
        val current = _uiState.value.clients
        _uiState.value = _uiState.value.copy(
            clients = listOf(client) + current.filterNot { it.id == client.id }
        )
    }

    private fun setError(error: Throwable) {
        HavnLogger.error("ClientsViewModel", "User-visible clients error", error)
        _uiState.value = _uiState.value.copy(
            error = error.message?.takeIf { it.isNotBlank() } ?: "Something went wrong while loading contacts."
        )
    }
}

data class ClientsUiState(
    val isLoading: Boolean = false,
    val isLoadingMore: Boolean = false,
    val isDetailLoading: Boolean = false,
    val isSaving: Boolean = false,
    val error: String? = null,
    val message: String? = null,
    val clients: List<Client> = emptyList(),
    val searchQuery: String = "",
    val hasMoreClients: Boolean = false,
    val selectedClientId: String? = null,
    val mode: ClientsMode = ClientsMode.List,
    val form: ClientFormState = ClientFormState(),
    val commentsDraft: String = "",
    val isEditingComments: Boolean = false,
    val showDeleteConfirmation: Boolean = false
) {
    val selectedClient: Client?
        get() = clients.firstOrNull { it.id == selectedClientId }
}

data class ClientFormState(
    val firstName: String = "",
    val lastName: String = "",
    val email: String = "",
    val phone: String = "",
    val notes: String = "",
    val validationError: String? = null
) {
    fun validate(): String? = when {
        firstName.trim().isBlank() -> "First name is required."
        lastName.trim().isBlank() -> "Last name is required."
        email.trim().isBlank() && phone.trim().isBlank() -> "Add an email or phone number for QR delivery."
        email.isNotBlank() && !email.contains("@") -> "Enter a valid email address."
        else -> null
    }

    fun toDraft(): ClientDraft = ClientDraft(
        firstName = firstName.trim(),
        lastName = lastName.trim(),
        email = email.trim().takeIf { it.isNotBlank() },
        phone = phone.trim().takeIf { it.isNotBlank() },
        notes = notes.trim().takeIf { it.isNotBlank() }
    )
}

enum class ClientsMode {
    List,
    Add,
    Edit,
    Detail
}

private fun Client.toFormState(): ClientFormState = ClientFormState(
    firstName = firstName.orEmpty().ifBlank { fullName.substringBefore(" ") },
    lastName = lastName.orEmpty().ifBlank { fullName.substringAfter(" ", "") },
    email = email.orEmpty(),
    phone = phone.orEmpty(),
    notes = notes.orEmpty()
)

private const val CLIENT_PAGE_SIZE = 50
